import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { useUserContext } from "./UserContext";

const URLAPI = process.env.REACT_APP_BASE_URL ?? "";
const HUB_URL = URLAPI.replace(/\/api\/?$/i, "") + "/notificacionesHub";

type RealtimeHandler = (...args: any[]) => void;
type RealtimeContextType = { subscribe: (eventName: string, handler: RealtimeHandler) => () => void };

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const useRealtime = () => {
  const value = useContext(RealtimeContext);
  if (!value) throw new Error("useRealtime must be used within a RealtimeProvider");
  return value;
};

export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUserContext();
  const handlersRef = useRef<Map<string, Set<RealtimeHandler>>>(new Map());
  const connectionRef = useRef<signalR.HubConnection>();

  const subscribe = useCallback((eventName: string, handler: RealtimeHandler) => {
    const handlers = handlersRef.current.get(eventName) ?? new Set<RealtimeHandler>();
    handlers.add(handler);
    handlersRef.current.set(eventName, handlers);
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) handlersRef.current.delete(eventName);
    };
  }, []);

  useEffect(() => {
    if (!user?.token || !HUB_URL) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_URL}?clientType=web-empleado`, { accessTokenFactory: () => user.token ?? "" })
      .withAutomaticReconnect()
      .build();
    connectionRef.current = connection;

    const dispatch = (eventName: string) => (...args: any[]) =>
      handlersRef.current.get(eventName)?.forEach((handler) => handler(...args));
    const notificationDispatch = dispatch("NotificacionCreada");
    const productsDispatch = dispatch("ProductosVentaActualizados");
    const mesasDispatch = dispatch("MesasAbiertasActualizadas");
    const reconnectedDispatch = dispatch("RealtimeReconnected");
    connection.on("NotificacionCreada", notificationDispatch);
    connection.on("ProductosVentaActualizados", productsDispatch);
    connection.on("MesasAbiertasActualizadas", mesasDispatch);
    connection.onreconnected(reconnectedDispatch);
    connection.start().catch((error) => console.log(error));

    return () => {
      connection.off("NotificacionCreada", notificationDispatch);
      connection.off("ProductosVentaActualizados", productsDispatch);
      connection.off("MesasAbiertasActualizadas", mesasDispatch);
      connectionRef.current = undefined;
      connection.stop().catch((error) => console.log(error));
    };
  }, [user?.token]);

  const value = useMemo(() => ({ subscribe }), [subscribe]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};
